import React, { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 → `to` once it enters the viewport.
 * Uses requestAnimationFrame with cubic ease-out for a premium feel.
 *
 * Props:
 *  - to: target number (required)
 *  - duration: ms (default 1800)
 *  - prefix / suffix: optional strings around the number
 *  - format: function(n) => string  (defaults to localeString with commas)
 */
const Counter = ({
  to = 0,
  duration = 1800,
  prefix = "",
  suffix = "",
  format = (n) => n.toLocaleString(),
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      startedRef.current = true;
      setValue(to);
      return undefined;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            let raf = 0;
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.round(to * eased));
              if (t < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref} className={className} {...rest}>
      {prefix}
      {format(value)}
      {suffix}
    </span>
  );
};

export default Counter;
